import {
  actionPlanFields,
  actionPlanRoomListingFields,
} from '../ActionPlans/ActionPlanFields';
import {
  contentMediaVersionFields,
  contentRoomViewFields,
  contentViewFields,
} from '../Content/ContentFields';
import { getDealFields } from '../Deals/DealFields';
import { roomEditContactFields } from '../Contacts/ContactFields';
import { roomOrgFields } from '../Organization/OrganizationFields';
import { salesActionsFields } from '../SalesActions/SalesActionsFields';
import {
  signableFields,
  signableRoomListingFields,
} from '../Signables/SignableFields';

/**
 * Settings that could be known to guests when they visit a room
 */
const publicSettingsFields = `
  enableContactCard
  enableQa
  enableShare
  expiresAt
  invitedContactsOnly
  language
  layout
  passcode
  requiresEmail
  requiresExpirationDate
  requiresPasscode
  verifyEmail
`;

/**
 * All room settings
 */
const roomSettingsFields = `
  ${publicSettingsFields}
  thumbnailAssetId
  thumbnailUri
  thumbnailUrl
  notificationSettings {
    newVisitAlert
    newVisitEmail
    nudgeContacts
  }
`;

/**
 * All fields required for properly accessing and interacting with a room.
 *
 * Current use cases regarding rooms:
 *  - View room as admin/contact
 */
export const getRoomFields = (includeDspId = false): string => `
  ${roomSettingsFields}
  audience
  bannerAssetId
  bannerUri
  clientLogoUri
  clientLogoUrl
  description
  expired
  id
  link
  name
  organizationId
  ownerId
  roomContactsCount
  roomTemplateId
  contentTemplateIds
  buyerCopilotEnabled
  roomType
  status
  structure
  userLink
  uuid
  visitsCount
  metadata
  actionPlans {
    ${actionPlanFields}
  }
  buttons {
    ${salesActionsFields}
  }
  company {
    id
    name
  }
  contact {
    id
    anonymous
    crmId
    email
    firstName
    lastName
    linkedin
    mobilePhone
    company {
      name
    }
  }
  contents {
    ${contentViewFields}
  }
  createdBy {
    firstName
    lastName
  }
  deal {
    ${getDealFields(includeDspId)}
  }
  organization {
    ${roomOrgFields}
  }
  owner {
    id
    email
    firstName
    fullName
    lastName
    linkedin
    phoneNumber
    photo
    username
    member {
      jobTitle
      roleDescription
    }
  }
  permissions {
    canEdit
  }
  roomCollaborators {
    id
    collaboratorId
    lastAccessAt
  }
  signables {
    ${signableFields}
  }
  signablesStructure {
    id
    name
    visible
  }
  welcomeMessage {
    ${contentViewFields}
  }
`;

export const roomFields = getRoomFields(false);

/**
 * All fields required for properly accessing and interacting with a room.
 *
 * Current use cases regarding rooms:
 *  - Room (and shared links) overview page
 */
export const getRoomOverviewFields = (includeDspId = false): string => `
  ${roomSettingsFields}
  id
  audience
  domainsFilter
  exportable
  link
  name
  organizationId
  ownerId
  roomContactsCount
  roomType
  status
  structure
  totalItems
  userLink
  uuid
  visitorsCount
  visitsCount
  actionPlans {
    ${actionPlanFields}
  }
  buttons {
    ${salesActionsFields}
  }
  company {
    id
    name
  }
  contact {
    id
    anonymous
    crmId
    email
    firstName
    lastName
    linkedin
    mobilePhone
    company {
      name
    }
  }
  contents {
    ${contentMediaVersionFields}
  }
  deal {
    ${getDealFields(includeDspId)}
  }
  owner {
    id
    email
    firstName
    fullName
    lastName
    username
    status
    member {
      ownedPublishedDraftRooms
      jobTitle
    }
  }
  permissions {
    canEdit
  }
  roomCollaborators {
    id
    collaboratorId
    lastAccessAt
  }
  signables {
    ${signableFields}
  }
  signablesStructure {
    id
    name
    visible
  }
  welcomeMessage {
    ${contentMediaVersionFields}
  }
`;

/**
 * Default export for backward compatibility (used by other parts of codebase)
 */
export const roomOverviewFields = getRoomOverviewFields(false);

/**
 * All fields required for properly editing a room.
 *
 * Current use cases:
 *  - Create room
 *  - Update room
 */
export const roomEditFields = `
  ${roomFields}
  contact {
    ${roomEditContactFields}
  }
  roomTemplate {
    id
    personal
  }
`;

/**
 * Fields for accessing a room as an admin/contact. The difference with
 * roomFields is that for viewing a room we don't fetch its (primary) contact,
 * since we always need to perform a verification for anonymous guests
 * or if the room has email or passcode access security.
 *
 * Current use cases:
 *  - View room as admin/contact
 */
export const getRoomViewFields = (includeDspId = false): string => `
  ${publicSettingsFields}
  id
  audience
  bannerUri
  buyerCopilotEnabled
  clientLogoUri
  clientLogoUrl
  companyId
  conversationThreadsCount
  expired
  link
  name
  organizationId
  roomContactsCount
  roomObjectAclsContents
  roomTemplateId
  contentTemplateIds
  roomType
  status
  structure
  trackActivity
  uuid
  visitsCount
  actionPlans {
    ${actionPlanFields}
  }
  buttons {
    ${salesActionsFields}
  }
  contents {
    ${contentRoomViewFields}
  }
  createdBy {
    firstName
    lastName
    username
  }
  deal {
    ${getDealFields(includeDspId)}
  }
  organization {
    ${roomOrgFields}
    vendorId
  }
  owner {
    id
    email
    firstName
    fullName
    lastName
    linkedin
    phoneNumber
    photo
    username
    member {
      ownedPublishedDraftRooms
      jobTitle
    }
  }
  roomCollaborators {
    id
    collaboratorId
    email
    firstName
    jobTitle
    lastName
    linkedin
    phoneNumber
    photo
    roleDescription
    sellingTeam
    username
  }
  signables {
    ${signableFields}
  }
  signablesStructure {
    id
    name
    visible
  }
  welcomeMessage {
    ${contentRoomViewFields}
  }
`;

export const roomViewFields = getRoomViewFields(false);

/**
 * Minimum fields required for listing the existing rooms.
 *
 * Current use cases regarding rooms:
 *  - Rooms table
 */
export const getRoomListingFields = (includeDspId = false): string => `
  ${roomSettingsFields}
  id
  audience
  bannerUri
  contentIds
  conversationThreadsCount
  description
  exportable
  lastActivityAt
  link
  name
  ownerName
  ownerId
  roomContactsCount
  roomType
  status
  structure
  updatedAt
  userLink
  uuid
  visitorsCount
  visitsCount
  actionPlans {
    ${actionPlanRoomListingFields}
  }
  contact {
    id
    email
    firstName
    lastName
    mobilePhone
    phoneNumber
  }
  company {
    id
    name
  }
  deal {
    id
    amount
    crmId
    currency
    name${includeDspId ? '\n    dspId' : ''}
  }
  owner {
    id
    firstName
    lastName
    username
    status
    member {
      ownedPublishedDraftRooms
    }
  }
  permissions {
    canEdit
  }
  roomCollaborators {
    id
    collaboratorId
    lastAccessAt
  }
  signables {
    ${signableRoomListingFields}
  }
  signablesStructure {
    id
    name
  }
`;

/**
 * Default export for backward compatibility (used by other parts of codebase)
 */
export const roomListingFields = getRoomListingFields(false);

/**
 * Minimum fields required for getting rooms insights.
 *
 * Current use cases regarding rooms:
 *  - Insights > Rooms
 *  - Insights > Content Discovery
 */
export const roomInsightsFieldsOptimization = `
  id
  noOfItems
  createdAtEpoch
  createdById
  visitorsCount
  visitsCount
`;

/**
 * Current use cases:
 *  - Content link listing
 *  - Content link statistics
 */
export const roomSharedlinksListingFields = `
  ${roomSettingsFields}
  id
  expired
  exportable
  lastActivityAt
  link
  userLink
  name
  ownerId
  roomContactsCount
  roomType
  status
  structure
  updatedAt
  uuid
  visitorsCount
  visitsCount
  company {
    id
    name
  }
  owner {
    id
    firstName
    fullName
    lastName
    username
    status
    member {
      ownedPublishedDraftRooms
    }
  }
  permissions {
    canEdit
  }
  roomCollaborators {
    id
    collaboratorId
    lastAccessAt
  }
`;

// manage links
export const manageLinksFields = `
  ${roomSettingsFields}
  id
  expired
  lastActivityAt
  link
  name
  roomType
  status
  structure
  updatedAt
  uuid
  visitorsCount
  visitsCount
  company {
    id
    name
  }
`;

/**
 * Current use cases:
 *  - Chrome Extension > Rooms Table
 */
export const chromeExtensionRoomListingFields = `
  id
  link
  name
  roomContactsCount
  status
  updatedAt
  uuid
  contact {
    id
    email
    firstName
    lastName
    phoneNumber
  }
  company {
    id
    name
  }
  deal {
    amount
    name
  }
  organization {
    id
    tenantType
  }
  owner {
    id
    firstName
    lastName
    photo
  }
`;

export const roomCollaboratorsBaseFields = `
  id
  collaboratorId
  collaboratorType
  emailNotification
  firstName
  lastName
  sellingTeam
  username
`;

// get data for 3 dots menu
export const roomMenuFields = `
  id
  owner {
    id
  }
  roomCollaborators {
    ${roomCollaboratorsBaseFields}
  }

`;

// get data room collaborators
export const roomCollaboratorsFields = `
  roomCollaborators {
    ${roomCollaboratorsBaseFields}
    email
    jobTitle
    linkedin
    phoneNumber
    photo
    roleDescription
  }
`;

// get room data with room collaborators
export const basicRoomWithCollaboratorsFields = `
  ${roomSettingsFields}
  id
  actionPlans {
    id
    visibility
  }
  companyId
  contactId
  name
  structure
  owner {
    id
  }
  roomCollaborators {
    ${roomCollaboratorsBaseFields}
    email
    jobTitle
    linkedin
    phoneNumber
    photo
    roleDescription
  }
`;

/**
 * Minimum fields required to make a redirection to a room if the UUID
 * is available.
 *
 * Current use cases:
 *  - As a non logged-in user, open a room edit link
 */
export const roomRedirectionFields = `
  link
`;

/**
 * Minimum fields required to load a room in the view page. These are used
 * only for the very first initial request to fetch the room's status and
 * security settings. Some extra fields are fetched because they are required
 * for tracking purposes
 */
export const roomViewInitialFields = `
  ${publicSettingsFields}
  id
  audience
  bannerUri
  companyId
  expired
  link
  name
  organizationId
  roomContactsCount
  roomType
  status
  userLink
  uuid
  visitsCount
  actionPlans {
    id
  }
  buttons {
    id
  }
  contents {
    id
  }
  deal {
    id
  }
  organization {
    ${roomOrgFields}
    vendorId
  }
  owner {
    id
    email
    firstName
    fullName
    lastName
    linkedin
    phoneNumber
    photo
    username
    member {
      jobTitle
    }
  }
  roomCollaborators {
    id
  }
  signables {
    id
  }
  welcomeMessage {
    id
  }
  `;

/**
 * Minimum fields required for listing the room data.
 *
 * Current use cases regarding rooms:
 *  - Conversation mentions
 *  - Conversation maps mentions
 */
export const roomForMentionsFields = `
  id
  audience
  contact {
    id
    avatarUri
    email
    firstName
    lastName
    mobilePhone
  }
  owner {
    id
    email
    firstName
    fullName
    lastName
    photo
    username
  }
`;

export const verifyRoomContactAccessFields = `
  pendingVerification
  contact {
    id
    anonymous
    avatarUri
    consent
    crmId
    email
    firstName
    lastName
    mobilePhone
  }
`;

export const verifyRoomUserAccessFields = `
  user {
    id
  }
`;
